import { z } from "zod";
import type { LessonContent } from "./content.js";

/**
 * Mode « défilement » : la leçon devient une suite de cartes plein écran (une étape = une carte),
 * que l'élève fait glisser vers le haut. Mêmes contenus que le mode « pas à pas » : rien à rédiger deux fois.
 */

export const LessonMode = z.enum(["STEPS", "FEED"]);
export type LessonMode = z.infer<typeof LessonMode>;

/** Une visite de leçon, résumée sur l'appareil (une ligne par visite, pas une par carte : volume maîtrisé). */
export const LessonVisit = z.object({
  id: z.string().uuid(),
  studentId: z.string().max(64),
  notionId: z.string().max(64),
  lang: z.string().min(2).max(8),
  contentVersion: z.number().int().min(0),
  mode: LessonMode,
  cards: z.array(z.object({ stepId: z.string().max(40), dwellMs: z.number().int().min(0).max(3_600_000) })).max(60),
  checks: z.array(z.object({ blockId: z.string().max(40), correct: z.boolean() })).max(60).default([]),
  help: z.array(z.string().max(40)).max(30).default([]), // cartes où l'élève a touché « Je n'ai pas compris »
  completed: z.boolean(),
  startedAt: z.string().datetime(),
  endedAt: z.string().datetime(),
});
export type LessonVisit = z.infer<typeof LessonVisit>;

/** Réponse à une carte de la révision du jour. */
export const ReviewAnswer = z.object({
  id: z.string().uuid(),
  studentId: z.string().max(64),
  notionId: z.string().max(64),
  cardKey: z.string().max(60), // "step:<id>" ou "quiz:<id>"
  correct: z.boolean(),
  occurredAt: z.string().datetime(),
});
export type ReviewAnswer = z.infer<typeof ReviewAnswer>;

export const stepKey = (stepId: string) => `step:${stepId}`;
export const quizKey = (blockId: string) => `quiz:${blockId}`;

/** Cartes d'une leçon qui reviennent en révision : étapes « à retenir » et questions rapides. */
export function reviewableKeys(lesson: Pick<LessonContent, "steps" | "blocks">): string[] {
  const keys = lesson.steps.filter((s) => s.recap).map((s) => stepKey(s.id));
  for (const b of lesson.blocks) if (b.type === "quiz") keys.push(quizKey(b.id));
  return keys;
}

/**
 * Répétition espacée, boîtes de Leitner : bonne réponse → boîte suivante (intervalle doublé),
 * erreur → retour en boîte 1 (revue le lendemain). Simple, explicable aux parents et aux enseignants.
 */
export const LEITNER_DAYS = [1, 2, 4, 8, 16, 32] as const;
export function leitner(box: number, correct: boolean, from: Date) {
  const next = correct ? Math.min(box + 1, LEITNER_DAYS.length) : 1;
  const due = new Date(from.getTime() + LEITNER_DAYS[next - 1] * 86_400_000);
  return { box: next, dueAt: due };
}

export const REVIEW_DAILY_MAX = 10;

/** Conseils d'écriture pour le mode défilement (alertes douces dans le studio, jamais bloquantes). */
export function feedLint(lesson: Pick<LessonContent, "steps" | "blocks">): { stepId: string | null; message: string }[] {
  const out: { stepId: string | null; message: string }[] = [];
  const byId = new Map(lesson.blocks.map((b) => [b.id, b]));
  const VISUAL = new Set(["formula", "figure", "geometry", "image", "solid3d", "scene3d", "video"]);
  let sinceQuiz = 0;
  lesson.steps.forEach((s, i) => {
    const blocks = s.blockIds.map((id) => byId.get(id)).filter(Boolean);
    const words = blocks.reduce((n, b) => n + (b && (b.type === "text" || b.type === "mistake") ? b.md.split(/\s+/).filter(Boolean).length : 0), 0);
    if (words > 40) out.push({ stepId: s.id, message: `Carte ${i + 1} : ${words} mots — visez 40 au plus pour une carte` });
    const visuals = blocks.filter((b) => b && VISUAL.has(b.type)).length;
    if (visuals > 1) out.push({ stepId: s.id, message: `Carte ${i + 1} : ${visuals} visuels — un seul par carte se lit mieux` });
    if (blocks.some((b) => b?.type === "quiz")) sinceQuiz = 0; else sinceQuiz++;
    if (sinceQuiz === 5) out.push({ stepId: s.id, message: `Carte ${i + 1} : 5 cartes sans question rapide — ajoutez-en une pour vérifier la compréhension` });
  });
  if (lesson.steps.length > 0 && !lesson.steps.some((s) => s.recap)) out.push({ stepId: null, message: "Aucune carte « à retenir » : la révision du jour n'aura rien à proposer pour cette leçon" });
  return out;
}
